import { memo, useMemo } from 'react';
import { ui } from '@procuros/datachecks';

import { DisplayModes } from 'services/webedi/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { DataTable, useGenerateWebEDIColumns } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';

import { DocumentSectionAnnotation } from '../DocumentSectionAnnotation/DocumentSectionAnnotation';
import { usePinnedColumns } from '../helpers';
import { WebEDISection } from '../WebEDISection/WebEDISection';

const DATA_PREFIX = 'parties';

type DocumentPartiesProps = {
  config: ui.PartiesSection;
  mode: DisplayModes;
  isSubmitting: boolean;
  baseData?: Array<unknown>;
  revisedData?: Array<unknown>;
};

export const DocumentParties = memo(({ config, mode, isSubmitting, baseData, revisedData }: DocumentPartiesProps) => {
  const processSpecificationFields = useWebEDIStore((state) => state.processSpecificationFields);
  const { label: title, description, fields, options } = config;
  const labels = useMemo(() => {
    const partiesType = processSpecificationFields?.filter((field: any) => {
      const containsTwoDots = (field.path.split('.') || []).length === 2;
      return containsTwoDots && field.path.includes('parties');
    });
    return (
      partiesType?.reduce((acc: any, field: any) => {
        acc[field.path.split('.')[1]] = field.name;
        return acc;
      }, {}) || {}
    );
  }, [processSpecificationFields]);

  if (!fields?.length) {
    return null;
  }

  return (
    <WebEDISection header={{ title, spacing: 'medium', description }}>
      <div className="space-y-4">
        <DocumentSectionAnnotation path={DATA_PREFIX} sectionConfig={config} />
        <DataTableProvider name={DATA_PREFIX} baseData={baseData} revisedData={revisedData}>
          <PartiesTable
            numberOfPinnedColumns={options?.pinned_column_count ?? 2}
            config={config}
            mode={mode}
            labels={labels}
            isSubmitting={isSubmitting}
          />
        </DataTableProvider>
      </div>
    </WebEDISection>
  );
});

DocumentParties.displayName = 'DocumentParties';

const PartiesTable = ({
  mode,
  config,
  numberOfPinnedColumns,
  labels,
  isSubmitting,
}: {
  mode: DisplayModes;
  config: ui.PartiesSection;
  numberOfPinnedColumns: number | undefined;
  labels: Record<string, string>;
  isSubmitting: boolean;
}) => {
  const { fields, baseData, revisedData } = useDataTableContext();
  const updatedConfig = useMemo(() => {
    return config.fields.map((field, index) => {
      if (index === 0) {
        const extraFields = {
          translateValue: (value: string) => labels[value],
        };
        return {
          ...field,
          extraFields,
        };
      }
      return field;
    });
  }, [config, labels]);

  const columns = useGenerateWebEDIColumns({ config: updatedConfig, displayMode: mode });
  const pinnedColumns = usePinnedColumns({ config: updatedConfig, numberOfPinnedColumns });

  return (
    <DataTable
      pinnedColumns={pinnedColumns}
      key={mode}
      meta={{ dataPrefix: DATA_PREFIX, mode, isSubmitting }}
      columns={columns}
      data={fields}
      baseData={baseData as typeof fields}
      revisedData={revisedData as typeof fields}
      enableGlobalSearch={false}
    />
  );
};
